import { useState } from 'react';
import { Avatar, Empty, Input, Space, Switch, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import { AppstoreOutlined, SearchOutlined } from '@ant-design/icons';
import type { DeviceAppReportDto, DeviceDetailDto } from '@/types/device';

const { Text } = Typography;

interface Props {
  device: DeviceDetailDto;
}

/**
 * Mapeo del campo state que reporta Google para apps:
 *   INSTALLED, REMOVED, UNSPECIFIED
 */
const APP_STATE_LABELS: Record<string, string> = {
  INSTALLED: 'Instalada',
  REMOVED: 'Removida',
};

const APP_STATE_COLORS: Record<string, string> = {
  INSTALLED: 'success',
  REMOVED: 'default',
};

/**
 * Mapeo del campo applicationSource:
 *   SYSTEM_APP_FACTORY_VERSION = pre-instalada
 *   SYSTEM_APP_UPDATED_VERSION = pre-instalada actualizada
 *   INSTALLED_FROM_PLAY_STORE = de Play Store
 *   ...
 */
const SOURCE_LABELS: Record<string, string> = {
  SYSTEM_APP_FACTORY_VERSION: 'Sistema (factory)',
  SYSTEM_APP_UPDATED_VERSION: 'Sistema (updated)',
  INSTALLED_FROM_PLAY_STORE: 'Play Store',
  APPLICATION_SOURCE_UNSPECIFIED: '—',
};

export function DeviceAppsTab({ device }: Props) {
  const [search, setSearch] = useState('');
  const [onlyInProfile, setOnlyInProfile] = useState(false);
  const [hideSystem, setHideSystem] = useState(true);

  if (device.installedApps.length === 0) {
    return (
      <Empty
        description={
          device.isEnrolledInGoogle
            ? 'Google aun no reporto las apps instaladas. Probar el comando "Sincronizar".'
            : 'Device no enrolado. Las apps se listaran cuando Google reporte.'
        }
      />
    );
  }

  // Filtros
  const filtered = device.installedApps.filter((app) => {
    if (onlyInProfile && !app.isInProfileCatalog) return false;
    if (hideSystem && app.installationSource?.startsWith('SYSTEM_APP')) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        app.packageName.toLowerCase().includes(s) ||
        (app.displayName?.toLowerCase().includes(s) ?? false)
      );
    }
    return true;
  });

  const columns: TableProps<DeviceAppReportDto>['columns'] = [
    {
      title: 'App',
      key: 'app',
      render: (_, app) => (
        <Space>
          {app.iconUrl ? (
            <Avatar src={app.iconUrl} size={32} shape="square" />
          ) : (
            <Avatar size={32} shape="square" icon={<AppstoreOutlined />} />
          )}
          <Space direction="vertical" size={0}>
            <Text strong>{app.displayName || app.packageName}</Text>
            <Text code style={{ fontSize: 11 }}>{app.packageName}</Text>
          </Space>
        </Space>
      ),
      sorter: (a, b) => (a.displayName || a.packageName).localeCompare(b.displayName || b.packageName),
    },
    {
      title: 'Version',
      key: 'version',
      width: 130,
      render: (_, app) => (
        <Space direction="vertical" size={0}>
          {app.versionName ? <Text>{app.versionName}</Text> : <Text type="secondary">—</Text>}
          {app.versionCode != null && <Text type="secondary" style={{ fontSize: 11 }}>build {app.versionCode}</Text>}
        </Space>
      ),
    },
    {
      title: 'Origen',
      dataIndex: 'installationSource',
      key: 'source',
      width: 160,
      render: (v) => v ? <Tag>{SOURCE_LABELS[v] || v}</Tag> : <Text type="secondary">—</Text>,
      filters: Object.keys(SOURCE_LABELS).map((k) => ({ text: SOURCE_LABELS[k], value: k })),
      onFilter: (value, app) => app.installationSource === value,
    },
    {
      title: 'Estado',
      dataIndex: 'state',
      key: 'state',
      width: 110,
      render: (v) => v ? <Tag color={APP_STATE_COLORS[v] ?? 'default'}>{APP_STATE_LABELS[v] ?? v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'En perfil',
      key: 'inProfile',
      width: 110,
      align: 'center',
      render: (_, app) => app.isInProfileCatalog
        ? <Tag color="blue">Si</Tag>
        : <Text type="secondary">No</Text>,
      sorter: (a, b) => (a.isInProfileCatalog === b.isInProfileCatalog ? 0 : a.isInProfileCatalog ? -1 : 1),
    },
  ];

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Space wrap>
        <Input
          placeholder="Buscar por nombre o package..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280 }}
          allowClear
        />
        <Space>
          <Text>Solo del perfil:</Text>
          <Switch checked={onlyInProfile} onChange={setOnlyInProfile} size="small" />
        </Space>
        <Space>
          <Text>Ocultar apps del sistema:</Text>
          <Switch checked={hideSystem} onChange={setHideSystem} size="small" />
        </Space>
      </Space>

      <Table<DeviceAppReportDto>
        rowKey="packageName"
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (total) => `${total} apps` }}
        size="small"
      />
    </Space>
  );
}
